export interface SharedLoad<T> {
    /** The value. The first call starts the load and later calls share it. */
    get: () => Promise<T>;
    /** Loads the value again and updates it in place. Does nothing while nothing has loaded it. */
    reload: () => Promise<void>;
}

export const RETRY_DELAY_MS = 250;

/**
 * A 502 or 503: the server or its gateway was briefly unavailable, worth one more try. Not a 504
 * (that already waited for the gateway's timeout), a 4xx, or anything without an HTTP status: both
 * API clients turn a request that got no answer into another error, so that isn't retried.
 */
const isTransient = (error: unknown) => {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;

    return status === 502 || status === 503;
};

const retryTransientOnce = async <R>(attempt: () => Promise<R>): Promise<R> => {
    try {
        return await attempt();
    } catch (error) {
        if (!isTransient(error)) {
            throw error;
        }

        await new Promise((resolve) => { setTimeout(resolve, RETRY_DELAY_MS); });

        return attempt();
    }
};

/**
 * One load of a value that several components share.
 *
 * - Loads and reloads that fail with a 502/503 are tried once more, because all components that
 *   share them would fail together. A load that still fails is forgotten, so the
 *   next get() tries again.
 * - Reloads run one after another, so an older response can never overwrite a newer one, and a failed
 *   reload doesn't stop the ones after it. Reloads asked for while another is still waiting share it,
 *   and a reload asked for during the first load runs once that load has finished.
 * - `update` refreshes the loaded value in place, so everyone holding it sees the new data. Without
 *   `update` the value is loaded once and never reloaded.
 */
export default function createSharedLoad<T>(load: () => Promise<T>, update?: (current: T) => Promise<void>): SharedLoad<T> {
    let value: Promise<T> | null = null;
    let loaded = false;
    let reloads: Promise<void> = Promise.resolve();
    let waitingReload: Promise<void> | null = null;

    const get = () => {
        if (!value) {
            const loading = retryTransientOnce(load);
            value = loading;
            loading.then(
                () => {
                    loaded = true;
                },
                () => {
                    if (value === loading) {
                        value = null;
                    }
                },
            );
        }

        return value;
    };

    const reload = (): Promise<void> => {
        if (!update || !value) {
            return Promise.resolve();
        }

        if (!loaded) {
            // The first load was asked for before this change: reload once it has finished.
            return value.then(() => reload(), () => undefined);
        }

        if (waitingReload) {
            return waitingReload;
        }

        const current = value;
        const run = reloads.then(async () => {
            waitingReload = null;
            const loadedValue = await current;
            await retryTransientOnce(() => update(loadedValue));
        });
        waitingReload = run;
        reloads = run.catch(() => undefined);

        return run;
    };

    return { get, reload };
}
